import { FC } from "react";
import { Link as RouterLink, useFetcher, useParams } from "react-router";
import {
  Alert,
  Badge,
  Box,
  Button,
  Link,
  Text,
} from "@postenbring/hedwig-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck } from "@fortawesome/pro-solid-svg-icons/faCircleCheck";
import { faXmark } from "@fortawesome/pro-solid-svg-icons/faXmark";
import Translated from "../infrastructure/translation/Translated";
import { CenteredSpinner } from "../CenteredSpinner/CenteredSpinner";
import styles from "./ClientPage.module.css";
import { useApplications } from "../infrastructure/entrypoint/EntrypointContext";
import { useTranslate } from "../infrastructure/translation/useTranslate";
import {
  isClientActionFailure,
  lacksActiveCertificate,
  useAvailableAccounts,
  useClient,
  useClientCertificates,
} from "./clientsRouteContext";
import { useClientPaths } from "./useClientPaths";
import { parseScopes, scopeOverview } from "./scopeUtils";
import { DetailList, DetailRow } from "../DetailList/DetailList";
import { CopyButton } from "./CopyButton";
import { BackLink } from "../BackLink/BackLink";
import { ClientOAuthDetails } from "./ClientOAuthDetails";
import { statusOf } from "../Certificate/certificateStatus";
import { faCircleMinus } from "@fortawesome/pro-solid-svg-icons/faCircleMinus";

export const ClientPage: FC = () => {
  const { clientId } = useParams<{ clientId: string }>();
  const translate = useTranslate();
  const paths = useClientPaths();
  const applications = useApplications();

  const client = useClient(clientId);
  const certificates = useClientCertificates();
  const availableAccounts = useAvailableAccounts();
  const fetcher = useFetcher();

  const deleting = fetcher.state !== "idle";
  const deleteError = isClientActionFailure(fetcher.data)
    ? fetcher.data.message
    : null;

  const handleDelete = () => {
    const confirmMessage = translate("client.delete_confirm_message");
    if (!window.confirm(confirmMessage)) {
      return;
    }

    fetcher.submit(null, { method: "delete" });
  };

  if (deleting) {
    return (
      <div className={styles.container}>
        <CenteredSpinner />
      </div>
    );
  }

  if (!client) {
    return (
      <div className={styles.container}>
        <p className={styles.error}>
          <Translated textKey={"client.not_found"} />
        </p>
        <Link asChild>
          <RouterLink to={`${paths.organization}/clients`}>
            <Translated textKey={"generic.to_overview"} />
          </RouterLink>
        </Link>
      </div>
    );
  }

  const scopes = scopeOverview(
    applications,
    availableAccounts,
    parseScopes(client.client.scopes),
  );

  const hasNoActiveCertificate = lacksActiveCertificate(client, certificates);

  return (
    <div className={styles.container}>
      <Text variant="h1" as="h1" size="min" className={styles.title}>
        <Translated textKey={"client.details_header"} />
      </Text>

      <Box variant="white" className={styles.card}>
        <div className={styles.cardTop}>
          <BackLink to={`${paths.organization}/clients`} />
          <button
            type="button"
            className={styles.deleteButton}
            onClick={handleDelete}
          >
            <FontAwesomeIcon icon={faXmark} aria-hidden />
            <Translated textKey={"client.delete"} />
          </button>
        </div>

        {hasNoActiveCertificate && (
          <Alert data-color="warning" role="alert">
            <Alert.Description>
              <Translated textKey={"client.no_active_certificates_warning"} />
            </Alert.Description>
          </Alert>
        )}

        <DetailList>
          <DetailRow label={<Translated textKey={"client.display_name"} />}>
            {client.client.displayName}
          </DetailRow>

          <DetailRow label={<Translated textKey={"client.client_id"} />}>
            <span className={styles.clientIdValue}>
              <span className={styles.clientId}>{client.client.clientId}</span>
              <CopyButton
                value={client.client.clientId}
                labelKey="client.copy_client_id"
              />
            </span>
          </DetailRow>

          <DetailRow label={<Translated textKey={"client.certificates"} />}>
            {certificates.length === 0 ? (
              <span className={styles.noScopes}>
                <Translated textKey={"client.no_certificates"} />
              </span>
            ) : (
              <div className={styles.certificateList}>
                {certificates.map((certificate) => {
                  const { isActive, certStatus } = statusOf(certificate);

                  return (
                    <span
                      key={certificate.certificate.id}
                      className={styles.certificateRow}
                    >
                      <Link asChild>
                        <RouterLink
                          to={`${paths.organization}/certificates/${certificate.certificate.id}`}
                        >
                          {certificate.certificate.displayName}
                        </RouterLink>
                      </Link>
                      {!isActive && (
                        <Badge data-color="error">
                          <Translated
                            textKey={`certificate.status_${certStatus}`}
                          />
                        </Badge>
                      )}
                    </span>
                  );
                })}
              </div>
            )}
          </DetailRow>

          <DetailRow label={<Translated textKey={"client.scopes"} />}>
            {scopes.length === 0 ? (
              <span className={styles.noScopes}>
                <Translated textKey={"client.no_scopes"} />
              </span>
            ) : (
              <div className={styles.scopeGroups}>
                {scopes.map((application) => (
                  <div key={application.oAuthId} className={styles.scopeGroup}>
                    <strong>{application.displayName}</strong>
                    {application.accounts.map((account) => (
                      <div key={account.accountId} className={styles.scopeRow}>
                        <FontAwesomeIcon
                          icon={account.granted ? faCircleCheck : faCircleMinus}
                          aria-hidden
                          className={
                            account.granted
                              ? styles.scopeIconActive
                              : styles.scopeIconInactive
                          }
                        />
                        <span className={styles.scopeName}>
                          {account.name ?? translate("client.account_id")}
                        </span>
                        <span className={styles.idChip}>
                          <Translated textKey={"client.account_id"} /> :{" "}
                          {account.accountId}
                        </span>
                        <span className={styles.scopeStatus}>
                          <Translated
                            textKey={
                              account.granted
                                ? "client.scope_active"
                                : "client.scope_inactive"
                            }
                          />
                        </span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </DetailRow>
        </DetailList>

        {deleteError && <p className={styles.error}>{deleteError}</p>}

        <div className={styles.actions}>
          <Button size="small" asChild>
            <RouterLink to={paths.editClient(client.client.clientId)}>
              <Translated textKey={"client.edit"} />
            </RouterLink>
          </Button>
        </div>
      </Box>

      <ClientOAuthDetails client={client.client} />
    </div>
  );
};
